import {
  AccountKind,
  AccountStatus,
  AdminConversationDto,
  AdminPermission,
  AdminUserBreadcrumbActivityDto,
  AdminStickerDto,
  AdminTicketListItemDto,
  AuditLogDto,
  AuditLogEventType,
  BlockDto,
  formatCoordinates,
  LikeDto,
  PhotoVerificationAttemptDetailDto,
  PhotoVerificationAttemptListItemDto,
  UserDetailDto,
  VisitDto,
  MyPlacesListDto,
  MyPlaceProximityAction,
  CommentDto,
} from "@ivy-monorepo/shared"
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import Avatar from "src/components/Avatar";
import FormItem from "src/components/FormItem";
import Icon from "src/components/Icon";
import LocationView from "src/components/locations/LocationView";
import Table from "src/components/Table";
import Pagination from "src/components/Pagination";
import ConversationDetailPopup from "src/components/ConversationDetail";
import FeedbackThreadModal from "src/components/FeedbackThreadModal";
import { adminService } from "src/services/admin.service"
import Modal from 'react-modal'
import VisitMap from "src/components/VisitMap";
import UnlockUserModal from "src/components/modals/UnlockUserModal";
import ClearLoginFailedAttemptsModal from "src/components/modals/ClearLoginFailedAttemptsModal";
import LockUserModal from "src/components/modals/LockUserModal";
import AccountAccessModal from "src/components/modals/AccountAccessModal";
import SendTestPushModal from "src/components/modals/SendTestPushModal";
import UserBreadcrumbActivityModal from "src/components/modals/UserBreadcrumbActivityModal";
import StickerModal from "src/components/modals/StickerModal";
import PhotoVerificationAttemptDetailModal from "src/components/modals/PhotoVerificationAttemptDetailModal";
import UserPhotoModal from "src/components/modals/UserPhotoModal";
import { toPhotoVerificationAttemptRow } from "src/helpers/photo-verification";
import { usePermissions } from "src/contexts/PermissionsContext";
import { formatTicketPrice } from "src/utils/ticketPrice";

type UserTabKey = 'details' | 'blocks' | 'conversations' | 'likes' | 'breadcrumbs' | 'stickers' | 'visits' | 'my-places' | 'vibe-history' | 'intentions' | 'photo-verification' | 'audit-logs' | 'feedback' | 'tickets';

const USER_INTENTION_AUDIT_EVENT_TYPES = `${AuditLogEventType.SetUserIntention},${AuditLogEventType.ClearUserIntention}`

const APP_USER_TABS: Array<{ key: UserTabKey; label: string; permission: AdminPermission }> = [
  { key: 'blocks', label: 'Blocks', permission: AdminPermission.Blocks },
  { key: 'likes', label: 'Likes', permission: AdminPermission.Likes },
  { key: 'conversations', label: 'Conversations', permission: AdminPermission.Conversations },
  { key: 'feedback', label: 'Feedback', permission: AdminPermission.Comments },
  { key: 'breadcrumbs', label: 'Breadcrumbs', permission: AdminPermission.Breadcrumbs },
  { key: 'stickers', label: 'Stickers', permission: AdminPermission.Stickers },
  { key: 'visits', label: 'Visits', permission: AdminPermission.Visits },
  { key: 'my-places', label: 'My Places', permission: AdminPermission.Users },
  { key: 'tickets', label: 'Tickets', permission: AdminPermission.Tickets },
  { key: 'vibe-history', label: 'Vibe', permission: AdminPermission.AuditLogs },
  { key: 'intentions', label: 'Intentions', permission: AdminPermission.Intentions },
  { key: 'photo-verification', label: 'Photo checks', permission: AdminPermission.PhotoVerification },
]

const ADMIN_ACCOUNT_TABS: Array<{ key: UserTabKey; label: string; permission: AdminPermission }> = [
  { key: 'audit-logs', label: 'Audit logs', permission: AdminPermission.AuditLogs },
]

const UserDetailPage = () => {
  const { publicId } = useParams<{ publicId: string }>()
  const navigate = useNavigate()
  const { hasPermission, isLoading: isPermissionsLoading } = usePermissions()
  const canManageAccountKind = hasPermission(AdminPermission.Admin)
  const canSendTestPush = hasPermission(AdminPermission.Notifications)
  const [userDetail, setUserDetail] = useState<UserDetailDto | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isVisibilityPending, setIsVisibilityPending] = useState(false)
  const [isAllowRemoteCheckInPending, setIsAllowRemoteCheckInPending] = useState(false)
  const [isPhotoVerifiedPending, setIsPhotoVerifiedPending] = useState(false)
  const [editedFirstName, setEditedFirstName] = useState('')
  const [editedLastName, setEditedLastName] = useState('')
  const [isUserNamePending, setIsUserNamePending] = useState(false)
  const [userNameError, setUserNameError] = useState<string | null>(null)
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false)
  const [isMyPlaceProximityActionPending, setIsMyPlaceProximityActionPending] = useState(false)
  const [isAccountAccessModalOpen, setIsAccountAccessModalOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<UserTabKey>('details')
  const [visitDetail, setVisitDetail] = useState<VisitDto | null>(null)
  const [selectedVisit, setSelectedVisit] = useState<string | null>(null)
  const [isUnlockModalOpen, setIsUnlockModalOpen] = useState(false)
  const [isClearLoginFailedAttemptsModalOpen, setIsClearLoginFailedAttemptsModalOpen] = useState(false)
  const [isLockModalOpen, setIsLockModalOpen] = useState(false)
  const [isTestPushModalOpen, setIsTestPushModalOpen] = useState(false)
  const [blocks, setBlocks] = useState<Array<BlockDto & { _key: string }>>([])
  const [conversations, setConversations] = useState<AdminConversationDto[]>([])
  const [conversationsPagination, setConversationsPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [conversationsPage, setConversationsPage] = useState(1)
  const [feedbackTopics, setFeedbackTopics] = useState<Array<CommentDto & { statusLabel: string }>>([])
  const [selectedFeedbackPublicId, setSelectedFeedbackPublicId] = useState<string | null>(null)
  const [likes, setLikes] = useState<Array<LikeDto & { _key: string }>>([])
  const [likesPagination, setLikesPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [likesPage, setLikesPage] = useState(1)
  const [breadcrumbActivity, setBreadcrumbActivity] = useState<
    Array<AdminUserBreadcrumbActivityDto & { _key: string; isFlagged: boolean }>
  >([])
  const [breadcrumbsPagination, setBreadcrumbsPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [breadcrumbsPage, setBreadcrumbsPage] = useState(1)
  const [stickerActivity, setStickerActivity] = useState<AdminStickerDto[]>([])
  const [stickerActivityPagination, setStickerActivityPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [stickerActivityPage, setStickerActivityPage] = useState(1)
  const [selectedStickerPublicId, setSelectedStickerPublicId] = useState<string | null>(null)
  const [tickets, setTickets] = useState<AdminTicketListItemDto[]>([])
  const [ticketsPagination, setTicketsPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [ticketsPage, setTicketsPage] = useState(1)
  const [visits, setVisits] = useState<VisitDto[]>([])
  const [visitsPagination, setVisitsPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [visitsPage, setVisitsPage] = useState(1)
  const [myPlaces, setMyPlaces] = useState<MyPlacesListDto | null>(null)
  const [auditLogs, setAuditLogs] = useState<AuditLogDto[]>([])
  const [auditLogsPagination, setAuditLogsPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [auditLogsPage, setAuditLogsPage] = useState(1)
  const [vibeHistory, setVibeHistory] = useState<AuditLogDto[]>([])
  const [vibeHistoryPagination, setVibeHistoryPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [vibeHistoryPage, setVibeHistoryPage] = useState(1)
  const [intentionHistory, setIntentionHistory] = useState<AuditLogDto[]>([])
  const [intentionHistoryPagination, setIntentionHistoryPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [intentionHistoryPage, setIntentionHistoryPage] = useState(1)
  const [photoVerificationAttempts, setPhotoVerificationAttempts] = useState<PhotoVerificationAttemptListItemDto[]>([])
  const [photoVerificationPagination, setPhotoVerificationPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [photoVerificationPage, setPhotoVerificationPage] = useState(1)
  const [selectedPhotoAttempt, setSelectedPhotoAttempt] = useState<PhotoVerificationAttemptDetailDto | null>(null)
  const [photoAttemptDetailLoading, setPhotoAttemptDetailLoading] = useState(false)
  const [photoAttemptVerifying, setPhotoAttemptVerifying] = useState(false)
  const [photoAttemptActionError, setPhotoAttemptActionError] = useState<string | null>(null)
  const [selectedActivity, setSelectedActivity] = useState<AdminUserBreadcrumbActivityDto | null>(null)
  const [selectedConversationPublicId, setSelectedConversationPublicId] = useState<string | null>(null)
  const [isConversationPopupVisible, setIsConversationPopupVisible] = useState(false)

  const isAppUser = userDetail?.kind === AccountKind.User
  const isAdminAccount = userDetail?.kind === AccountKind.Admin
  const userProfilePublicId = userDetail?.userPublicId ?? null
  const accountPublicId = userDetail?.publicId ?? null

  const visibleTabs = useMemo(() => {
    const tabs: Array<{ key: 'back' | UserTabKey; label: string }> = [
      { key: 'back', label: '←' },
      { key: 'details', label: 'Details' },
    ]

    if (isAppUser && !isPermissionsLoading) {
      tabs.push(
        ...APP_USER_TABS.filter(tab => hasPermission(tab.permission)).map(tab => ({
          key: tab.key,
          label: tab.label,
        }))
      )
    }

    if (isAdminAccount && !isPermissionsLoading) {
      tabs.push(
        ...ADMIN_ACCOUNT_TABS.filter(tab => hasPermission(tab.permission)).map(tab => ({
          key: tab.key,
          label: tab.label,
        }))
      )
    }

    return tabs
  }, [hasPermission, isAdminAccount, isAppUser, isPermissionsLoading])

  useEffect(() => {
    const fetchUserDetail = async () => {
      if (!publicId) {
        setError('User not found')
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)
        setError(null)
        const response = await adminService.getUserDetail(publicId)
        setUserDetail(response)
        setEditedFirstName(response.firstName ?? '')
        setEditedLastName(response.lastName ?? '')
        setUserNameError(null)
      } catch (err) {
        console.log('Failed to fetch user detail', err)
        setError('Failed to load user details.')
      } finally {
        setIsLoading(false)
      }
    }

    fetchUserDetail()
  }, [publicId])

  useEffect(() => {
    setActiveTab('details')
    setConversationsPage(1)
    setLikesPage(1)
    setBreadcrumbsPage(1)
    setVisitsPage(1)
    setAuditLogsPage(1)
    setVibeHistoryPage(1)
    setIntentionHistoryPage(1)
    setPhotoVerificationPage(1)
    setBlocks([])
    setConversations([])
    setLikes([])
    setBreadcrumbActivity([])
    setVisits([])
    setMyPlaces(null)
    setAuditLogs([])
    setVibeHistory([])
    setIntentionHistory([])
    setPhotoVerificationAttempts([])
    setSelectedPhotoAttempt(null)
    setPhotoAttemptActionError(null)
    setSelectedActivity(null)
  }, [publicId])

  useEffect(() => {
    if (!visibleTabs.some(tab => tab.key === activeTab)) {
      setActiveTab('details')
    }
  }, [activeTab, visibleTabs])

  useEffect(() => {
    if (!selectedVisit) {
      setVisitDetail(null)
      return
    }

    const fetchVisitDetail = async () => {
      try {
        const detail = await adminService.getVisitDetail(selectedVisit)
        setVisitDetail(detail)
      } catch (err) {
        console.log('Failed to fetch visit detail', err)
        setVisitDetail(null)
      }
    }

    fetchVisitDetail()
  }, [selectedVisit])

  useEffect(() => {
    if (activeTab !== 'blocks' || !userProfilePublicId) {
      return
    }

    const fetchBlocks = async () => {
      try {
        const response = await adminService.getBlocks(userProfilePublicId)
        setBlocks(
          response.map(block => ({
            ...block,
            _key:
              typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                ? crypto.randomUUID()
                : Math.random().toString(36).slice(2) + Date.now().toString(36),
          }))
        )
      } catch (err) {
        console.log('Failed to fetch user blocks', err)
      }
    }

    fetchBlocks()
  }, [activeTab, userProfilePublicId])

  const fetchConversations = useCallback(async () => {
    if (!userProfilePublicId) {
      return
    }

    try {
      const response = await adminService.getConversationsPaginated(
        conversationsPage,
        10,
        '',
        'createdAt',
        'desc',
        { userPublicId: userProfilePublicId },
      )
      setConversations(response.conversations)
      setConversationsPagination(response.pagination)
    } catch (err) {
      console.log('Failed to fetch user conversations', err)
    }
  }, [conversationsPage, userProfilePublicId])

  useEffect(() => {
    if (activeTab !== 'conversations' || !userProfilePublicId) {
      return
    }

    fetchConversations()
  }, [activeTab, fetchConversations, userProfilePublicId])

  const fetchFeedbackTopics = useCallback(async () => {
    if (!userProfilePublicId) {
      return
    }

    try {
      const response = await adminService.getUserFeedbackTopics(userProfilePublicId)
      setFeedbackTopics(
        response.map(comment => ({
          ...comment,
          statusLabel: comment.closedAt ? 'Closed' : 'Open',
        }))
      )
    } catch (err) {
      console.log('Failed to fetch user feedback', err)
    }
  }, [userProfilePublicId])

  useEffect(() => {
    if (activeTab !== 'feedback' || !userProfilePublicId) {
      return
    }

    fetchFeedbackTopics()
  }, [activeTab, fetchFeedbackTopics, userProfilePublicId])

  const fetchLikes = useCallback(async () => {
    if (!userProfilePublicId) {
      return
    }

    try {
      const response = await adminService.getLikes(
        likesPage,
        10,
        '',
        'likedAt',
        'desc',
        userProfilePublicId,
      )
      setLikes(
        response.likes.map(like => ({
          ...like,
          _key:
            typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
              ? crypto.randomUUID()
              : Math.random().toString(36).slice(2) + Date.now().toString(36),
        }))
      )
      setLikesPagination(response.pagination)
    } catch (err) {
      console.log('Failed to fetch user likes', err)
    }
  }, [likesPage, userProfilePublicId])

  useEffect(() => {
    if (activeTab !== 'likes' || !userProfilePublicId) {
      return
    }

    fetchLikes()
  }, [activeTab, fetchLikes, userProfilePublicId])

  const fetchBreadcrumbActivity = useCallback(async () => {
    if (!userProfilePublicId) {
      return
    }

    try {
      const response = await adminService.getUserBreadcrumbActivity(userProfilePublicId, breadcrumbsPage, 10)
      setBreadcrumbActivity(
        response.activity.map(item => ({
          ...item,
          isFlagged: !!item.isFlagged,
          _key:
            typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
              ? crypto.randomUUID()
              : Math.random().toString(36).slice(2) + Date.now().toString(36),
        }))
      )
      setSelectedActivity(current => {
        if (!current) {
          return current
        }

        const updated = response.activity.find(
          item => item.publicId === current.publicId && item.type === current.type
        )
        return updated ?? current
      })
      setBreadcrumbsPagination(response.pagination)
    } catch (err) {
      console.log('Failed to fetch user breadcrumb activity', err)
    }
  }, [breadcrumbsPage, userProfilePublicId])

  useEffect(() => {
    if (activeTab !== 'breadcrumbs' || !userProfilePublicId) {
      return
    }

    fetchBreadcrumbActivity()
  }, [activeTab, fetchBreadcrumbActivity, userProfilePublicId])

  const fetchStickerActivity = useCallback(async () => {
    if (!userProfilePublicId) {
      return
    }

    try {
      const response = await adminService.getUserStickerActivity(userProfilePublicId, stickerActivityPage, 10)
      setStickerActivity(response.activity)
      setStickerActivityPagination(response.pagination)
    } catch (err) {
      console.log('Failed to fetch user sticker activity', err)
    }
  }, [stickerActivityPage, userProfilePublicId])

  useEffect(() => {
    if (activeTab !== 'stickers' || !userProfilePublicId) {
      return
    }

    fetchStickerActivity()
  }, [activeTab, fetchStickerActivity, userProfilePublicId])

  const fetchTickets = useCallback(async () => {
    if (!userProfilePublicId) {
      return
    }

    try {
      const response = await adminService.getTickets(ticketsPage, 10, '', 'purchasedAt', 'desc', {
        userPublicId: userProfilePublicId,
      })
      setTickets(response.tickets.map(ticket => ({ ...ticket, priceLabel: formatTicketPrice(ticket.priceCents) })))
      setTicketsPagination(response.pagination)
    } catch (err) {
      console.log('Failed to fetch user tickets', err)
    }
  }, [ticketsPage, userProfilePublicId])

  useEffect(() => {
    if (activeTab !== 'tickets' || !userProfilePublicId || !hasPermission(AdminPermission.Tickets)) {
      return
    }

    fetchTickets()
  }, [activeTab, fetchTickets, hasPermission, userProfilePublicId])

  const fetchVisits = useCallback(async () => {
    if (!userProfilePublicId) {
      return
    }

    try {
      const response = await adminService.getVisits(
        visitsPage,
        10,
        '',
        'checkedInAt',
        'desc',
        undefined,
        userProfilePublicId,
      )
      setVisits(
        response.visits.map(visit => ({
          ...visit,
          venue: {
            ...visit.venue,
            logoUrl: visit.venue.logoUrl ?? `/images/venues/${visit.venue.type?.toLowerCase()}.png`,
          },
        }))
      )
      setVisitsPagination(response.pagination)
    } catch (err) {
      console.log('Failed to fetch user visits', err)
    }
  }, [userProfilePublicId, visitsPage])

  useEffect(() => {
    if (activeTab !== 'visits' || !userProfilePublicId) {
      return
    }

    fetchVisits()
  }, [activeTab, fetchVisits, userProfilePublicId])

  const fetchMyPlaces = useCallback(async () => {
    if (!userProfilePublicId) {
      return
    }
    try {
      const response = await adminService.getUserMyPlaces(userProfilePublicId)
      setMyPlaces(response)
    } catch (err) {
      console.log('Failed to fetch user my places', err)
      setMyPlaces(null)
    }
  }, [userProfilePublicId])

  useEffect(() => {
    if (activeTab !== 'my-places' || !userProfilePublicId) {
      return
    }
    fetchMyPlaces()
  }, [activeTab, fetchMyPlaces, userProfilePublicId])

  const fetchAccountAuditLogs = useCallback(async () => {
    if (!accountPublicId) {
      return
    }

    try {
      const response = await adminService.getAuditLogs(
        auditLogsPage,
        10,
        '',
        'desc',
        undefined,
        accountPublicId,
      )
      setAuditLogs(response.auditLogs)
      setAuditLogsPagination(response.pagination)
    } catch (err) {
      console.log('Failed to fetch account audit logs', err)
    }
  }, [accountPublicId, auditLogsPage])

  useEffect(() => {
    if (activeTab !== 'audit-logs' || !accountPublicId) {
      return
    }

    fetchAccountAuditLogs()
  }, [accountPublicId, activeTab, fetchAccountAuditLogs])

  const fetchVibeHistory = useCallback(async () => {
    if (!accountPublicId) {
      return
    }

    try {
      const response = await adminService.getAuditLogs(
        vibeHistoryPage,
        10,
        '',
        'desc',
        AuditLogEventType.SetVibeEmoji,
        accountPublicId,
      )

      const normalized = response.auditLogs.map((log) => {
        const anyLog = log as any
        const data = anyLog?.data
        if (typeof data === 'string') {
          try {
            return { ...anyLog, data: JSON.parse(data) }
          } catch {
            return anyLog
          }
        }
        return anyLog
      }) as AuditLogDto[]

      setVibeHistory(normalized)
      setVibeHistoryPagination(response.pagination)
    } catch (err) {
      console.log('Failed to fetch vibe history', err)
    }
  }, [accountPublicId, vibeHistoryPage])

  useEffect(() => {
    if (activeTab !== 'vibe-history' || !accountPublicId) {
      return
    }

    fetchVibeHistory()
  }, [accountPublicId, activeTab, fetchVibeHistory])

  const fetchIntentionHistory = useCallback(async () => {
    if (!accountPublicId) {
      return
    }

    try {
      const response = await adminService.getAuditLogs(
        intentionHistoryPage,
        10,
        '',
        'desc',
        USER_INTENTION_AUDIT_EVENT_TYPES,
        accountPublicId,
      )

      const normalized = response.auditLogs.map((log) => {
        const anyLog = log as any
        const data = anyLog?.data
        if (typeof data === 'string') {
          try {
            return { ...anyLog, data: JSON.parse(data) }
          } catch {
            return anyLog
          }
        }
        return anyLog
      }) as AuditLogDto[]

      setIntentionHistory(normalized)
      setIntentionHistoryPagination(response.pagination)
    } catch (err) {
      console.log('Failed to fetch intention history', err)
    }
  }, [accountPublicId, intentionHistoryPage])

  useEffect(() => {
    if (activeTab !== 'intentions' || !accountPublicId) {
      return
    }

    fetchIntentionHistory()
  }, [accountPublicId, activeTab, fetchIntentionHistory])

  const fetchPhotoVerificationAttempts = useCallback(async () => {
    if (!userProfilePublicId) return

    try {
      const response = await adminService.getUserPhotoVerificationAttempts(
        userProfilePublicId,
        photoVerificationPage,
        10,
      )
      setPhotoVerificationAttempts(response.attempts)
      setPhotoVerificationPagination(response.pagination)
    } catch (err) {
      console.log('Failed to fetch photo verification attempts', err)
    }
  }, [photoVerificationPage, userProfilePublicId])

  useEffect(() => {
    if (activeTab !== 'photo-verification' || !userProfilePublicId) {
      return
    }

    fetchPhotoVerificationAttempts()
  }, [activeTab, fetchPhotoVerificationAttempts, userProfilePublicId])

  useEffect(() => {
    if (selectedConversationPublicId) {
      setIsConversationPopupVisible(true)
    }
  }, [selectedConversationPublicId])


  const handleBackClick = () => {
    navigate(-1)
  }

  const handleCloseVisitModal = () => {
    setSelectedVisit(null)
  }

  const handleVisitsRowClick = (visit: VisitDto) => {
    setSelectedVisit(visit.publicId)
  }

  const handleConversationRowClick = (row: AdminConversationDto) => {
    setSelectedConversationPublicId(row.publicId)
  }

  const handleCloseConversationPopup = () => {
    setIsConversationPopupVisible(false)
    setTimeout(() => setSelectedConversationPublicId(null), 300)
  }

  const handleConversationsPageChange = (page: number) => {
    setConversationsPage(page)
  }

  const handleLikesPageChange = (page: number) => {
    setLikesPage(page)
  }

  const handleBreadcrumbsPageChange = (page: number) => {
    setBreadcrumbsPage(page)
  }

  const handleStickerActivityPageChange = (page: number) => {
    setStickerActivityPage(page)
  }

  const handleVisitsPageChange = (page: number) => {
    setVisitsPage(page)
  }

  const handleAuditLogsPageChange = (page: number) => {
    setAuditLogsPage(page)
  }

  const handleVibeHistoryPageChange = (page: number) => {
    setVibeHistoryPage(page)
  }

  const handleIntentionHistoryPageChange = (page: number) => {
    setIntentionHistoryPage(page)
  }

  const handlePhotoVerificationPageChange = (page: number) => {
    setPhotoVerificationPage(page)
  }

  const openPhotoAttemptDetail = async (attempt: PhotoVerificationAttemptListItemDto) => {
    setPhotoAttemptDetailLoading(true)
    setPhotoAttemptActionError(null)
    setSelectedPhotoAttempt(null)
    try {
      const detail = await adminService.getPhotoVerificationAttempt(attempt.publicId)
      setSelectedPhotoAttempt(detail)
    } catch (err) {
      console.log('Failed to fetch photo verification attempt detail', err)
    } finally {
      setPhotoAttemptDetailLoading(false)
    }
  }

  const closePhotoAttemptDetail = () => {
    setSelectedPhotoAttempt(null)
    setPhotoAttemptActionError(null)
  }

  const handleManualVerifyPhotoAttempt = async () => {
    if (!selectedPhotoAttempt?.canManuallyVerify) return
    setPhotoAttemptVerifying(true)
    setPhotoAttemptActionError(null)
    try {
      await adminService.manuallyVerifyPhoto(selectedPhotoAttempt.publicId)
      closePhotoAttemptDetail()
      await fetchPhotoVerificationAttempts()
      if (publicId) {
        const refreshed = await adminService.getUserDetail(publicId)
        setUserDetail(refreshed)
      }
    } catch (err: any) {
      const message =
        err?.response?.data?.userMessage
        || err?.response?.data?.message
        || 'Failed to manually verify this photo.'
      setPhotoAttemptActionError(message)
    } finally {
      setPhotoAttemptVerifying(false)
    }
  }

  const handleBreadcrumbRowClick = (row: AdminUserBreadcrumbActivityDto) => {
    setSelectedActivity(row)
  }

  const handleCloseBreadcrumbModal = () => {
    setSelectedActivity(null)
  }

  const handleSaveUserName = async () => {
    if (!publicId || !userDetail) return

    const nextFirstName = editedFirstName.trim()
    const nextLastName = editedLastName.trim()
    if (!nextFirstName || !nextLastName) {
      setUserNameError('First and last name are required')
      return
    }
    if (nextFirstName === userDetail.firstName && nextLastName === userDetail.lastName) {
      setUserNameError(null)
      return
    }

    setIsUserNamePending(true)
    setUserNameError(null)
    try {
      const updated = await adminService.updateUserName(publicId, nextFirstName, nextLastName)
      setUserDetail(updated)
      setEditedFirstName(updated.firstName ?? nextFirstName)
      setEditedLastName(updated.lastName ?? nextLastName)
    } catch (err) {
      console.error('Failed to update user name', err)
      setUserNameError('Failed to save name')
    } finally {
      setIsUserNamePending(false)
    }
  }

  const handleChangeAccountVisibility = async (e: ChangeEvent<HTMLSelectElement>) => {
    if (!publicId) return
    
    try {
      setIsVisibilityPending(true)
      const state = e.target.value === 'true'
      const userData = await adminService.updateUserVisibility(publicId, state)
      setUserDetail(userData)
    } catch(err) {
      console.log('Failed to fetch user detail', err)
      setError('Failed to update user Visibility status')
    } finally {
      setIsVisibilityPending(false)
    }
  }

  const handleChangeAllowRemoteCheckIn = async (e: ChangeEvent<HTMLSelectElement>) => {
    if (!publicId) return

    try {
      setIsAllowRemoteCheckInPending(true)
      const state = e.target.value === 'true'
      const userData = await adminService.updateAllowRemoteCheckIn(publicId, state)
      setUserDetail(userData)
    } catch (err) {
      console.log('Failed to update allow remote check-in', err)
      setError('Failed to update allow remote check-in status')
    } finally {
      setIsAllowRemoteCheckInPending(false)
    }
  }

  const handleChangePhotoVerified = async (e: ChangeEvent<HTMLSelectElement>) => {
    if (!publicId) return

    try {
      setIsPhotoVerifiedPending(true)
      const state = e.target.value === 'true'
      const userData = await adminService.updateUserPhotoVerified(publicId, state)
      setUserDetail(userData)
    } catch (err) {
      console.log('Failed to update photo verified status', err)
      setError('Failed to update photo verified status')
    } finally {
      setIsPhotoVerifiedPending(false)
    }
  }

  const handleChangeMyPlaceProximityAction = async (e: ChangeEvent<HTMLSelectElement>) => {
    if (!publicId) return

    try {
      setIsMyPlaceProximityActionPending(true)
      const action = e.target.value as MyPlaceProximityAction
      const userData = await adminService.updateMyPlaceProximityAction(publicId, action)
      setUserDetail(userData)
    } catch (err) {
      console.log('Failed to update My Place proximity action', err)
      setError('Failed to update My Place proximity action')
    } finally {
      setIsMyPlaceProximityActionPending(false)
    }
  }

  const pageTitle = !userDetail?.firstName
    ? 'Account Detail'
    : `${userDetail.firstName} ${userDetail.lastName}`

  return (
    <div className="user-detail-page">
      <div className="user-detail-header">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <div className="left-info-wrapper">
            <div className="avatar-wrapper">
              <Avatar
                firstName={userDetail?.firstName}
                lastName={userDetail?.lastName}
                url={userDetail?.imageUrl}
              />
              {isAppUser && userDetail && (
                <button
                  className="change-avatar"
                  title="Change user's photo"
                  onClick={() => setIsPhotoModalOpen(true)}
                >
                  <Icon name={'Upload'} />
                </button>
              )}
            </div>
            <h2>{pageTitle}</h2>
          </div>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {canSendTestPush && isAppUser && userDetail && (
              <button
                type="button"
                className="styled-button sm"
                disabled={isAccountAccessModalOpen || isLockModalOpen || isUnlockModalOpen || isClearLoginFailedAttemptsModalOpen || isTestPushModalOpen}
                onClick={() => setIsTestPushModalOpen(true)}
              >
                <span>Send test push</span>
              </button>
            )}
            {canManageAccountKind && userDetail && (
              <button
                type="button"
                className="styled-button sm"
                disabled={isAccountAccessModalOpen || isLockModalOpen || isUnlockModalOpen || isClearLoginFailedAttemptsModalOpen || isTestPushModalOpen}
                onClick={() => setIsAccountAccessModalOpen(true)}
              >
                <span>Account access</span>
              </button>
            )}
            {!userDetail?.lockedOutAt && userDetail && userDetail.status !== AccountStatus.Deleted && (
              <button
                type="button"
                className="styled-button sm"
                disabled={isAccountAccessModalOpen || isLockModalOpen || isUnlockModalOpen || isClearLoginFailedAttemptsModalOpen || isTestPushModalOpen}
                onClick={() => setIsLockModalOpen(true)}
              >
                <span>{isAppUser ? 'Lock user' : 'Lock account'}</span>
              </button>
            )}
            {!!userDetail?.lockedOutAt && (
              <button
                type="button"
                className="styled-button sm"
                disabled={isAccountAccessModalOpen || isUnlockModalOpen || isLockModalOpen || isClearLoginFailedAttemptsModalOpen || isTestPushModalOpen}
                onClick={() => setIsUnlockModalOpen(true)}
              >
                <span>{isAppUser ? 'Unlock user' : 'Unlock account'}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="tab-bar" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '24px' }}>
          {visibleTabs.map(tab => {
            const isActive = tab.key !== 'back' && tab.key === activeTab

            const handleTabClick = () => {
              if (tab.key === 'back') {
                handleBackClick()
                return
              }
              setActiveTab(tab.key as UserTabKey)
            }

            const classNames = ['tab']

            if (isActive) {
              classNames.push('active')
            }

            return (
              <button key={tab.key} type="button" className={classNames.join(' ')} onClick={handleTabClick}>
                {tab.label}
              </button>
            )
          })}
        </div>
      </div>

      {error && !isLoading && <div className="page-error">{error}</div>}

      {!isLoading && !error && userDetail && (
        <div className="user-detail-content">
          {activeTab === 'details' && (
            <>
              <div className="user-detail-overview">
                <div className="user-detail-info grid">
                  <FormItem label="Public ID" value={userDetail.publicId} />
                  <FormItem label="Status" value={`${userDetail.status} ${userDetail.lockedOutAt ? '(Locked)' : ''}`} />
                  {
                    !!userDetail.lockedOutAt
                    ? <FormItem label="Locked out at" value={userDetail.lockedOutAt} type="datetime" />
                    : <FormItem label="Locked out at" value={'Not locked'} />
                  }
                  <FormItem
                    label="Failed login attempts"
                    value={String(userDetail.failedAuthenticationCount ?? 0)}
                    formattedValue={
                      <>
                        <span>{userDetail.failedAuthenticationCount ?? 0}</span>
                        {(userDetail.failedAuthenticationCount ?? 0) > 0 && userDetail.status !== AccountStatus.Deleted && (
                          <button
                            type="button"
                            className="moderation-icon-button"
                            title="Clear failed login attempts"
                            aria-label="Clear failed login attempts"
                            disabled={isClearLoginFailedAttemptsModalOpen}
                            onClick={(e) => {
                              e.stopPropagation()
                              setIsClearLoginFailedAttemptsModalOpen(true)
                            }}
                          >
                            <Icon name="ArrowsRotate" />
                          </button>
                        )}
                      </>
                    }
                  />
                  <li className="form-item user-name-editor">
                    <label>Name</label>
                    <div className="user-name-editor-row">
                      <input
                        value={editedFirstName}
                        placeholder="First name"
                        onChange={e => {
                          setEditedFirstName(e.target.value)
                          if (userNameError) setUserNameError(null)
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            void handleSaveUserName()
                          }
                        }}
                        disabled={isUserNamePending}
                        maxLength={40}
                      />
                      <input
                        value={editedLastName}
                        placeholder="Last name"
                        onChange={e => {
                          setEditedLastName(e.target.value)
                          if (userNameError) setUserNameError(null)
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            void handleSaveUserName()
                          }
                        }}
                        disabled={isUserNamePending}
                        maxLength={40}
                      />
                      <button
                        type="button"
                        className="styled-button sm"
                        onClick={() => void handleSaveUserName()}
                        disabled={
                          isUserNamePending ||
                          editedFirstName.trim().length === 0 ||
                          editedLastName.trim().length === 0 ||
                          (editedFirstName.trim() === (userDetail.firstName ?? '') &&
                            editedLastName.trim() === (userDetail.lastName ?? ''))
                        }
                      >
                        <span>{isUserNamePending ? 'Saving…' : 'Save'}</span>
                      </button>
                    </div>
                    {userNameError && <p className="user-name-editor-error">{userNameError}</p>}
                  </li>
                  <FormItem label="Telephone" value={userDetail.telephone || '—'} />
                  <FormItem label="Email" value={userDetail.email} />
                  {isAppUser && <FormItem label="Date of birth" value={userDetail.dateOfBirth} />}
                  {isAppUser && <FormItem label="Device Type" value={userDetail.deviceType} />}
                  {isAppUser && (
                    <FormItem
                      label="OneSignal token"
                      value={
                        userDetail.hasValidOneSignalToken
                          ? 'Ready'
                          : userDetail.hasDeviceToken
                            ? 'Invalid'
                            : 'Missing'
                      }
                    />
                  )}
                  <FormItem label="Created" value={userDetail.createdAt} type="datetime" />
                  <FormItem label="Last Login" value={userDetail.lastLoginAt} type="datetime" />
                  <FormItem label="Account kind" value={userDetail.kind} />
                  <FormItem
                    label="Role"
                    value={
                      userDetail.kind !== AccountKind.User
                        ? userDetail.roles[0]?.name ?? '—'
                        : '—'
                    }
                  />
                </div>
                {isAppUser && (
                <div className="user-detail-info grid">
                  <LocationView
                    latitude={userDetail.latitude}
                    longitude={userDetail.longitude}
                    renderTrigger={open => (
                      <FormItem
                        label="Location"
                        value={formatCoordinates(userDetail.latitude, userDetail.longitude)}
                        onClick={open}
                      />
                    )}
                  />
                  <FormItem label="Age" value={userDetail.age} />
                  <div className="form-group form-item">
                    <label>Photo verified</label>
                    <select
                      value={String(userDetail?.isPhotoVerified ?? false)}
                      onChange={handleChangePhotoVerified}
                      disabled={isPhotoVerifiedPending}
                    >
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </select>
                  </div>
                  <FormItem
                    label="Gender Preference"
                    value={[userDetail.gender, userDetail.preferredGenders]}
                    type="genderPreference"
                  />
                  <div className="form-group form-item">
                    <label>Account Status</label>
                    <select
                      value={String(userDetail?.isInternal ?? 'false')}
                      onChange={handleChangeAccountVisibility}
                      disabled={isVisibilityPending}
                    >
                      <option value='false'>Public User</option>
                      <option value="true">Internal User</option>
                    </select>
                  </div>
                  <div className="form-group form-item">
                    <label>Allow remote check-ins</label>
                    <select
                      value={String(userDetail?.allowRemoteCheckIn ?? false)}
                      onChange={handleChangeAllowRemoteCheckIn}
                      disabled={isAllowRemoteCheckInPending}
                    >
                      <option value="false">Disabled</option>
                      <option value="true">Enabled</option>
                    </select>
                  </div>
                  <div className="form-group form-item">
                    <label>My Place proximity</label>
                    <select
                      value={userDetail?.myPlaceProximityAction ?? MyPlaceProximityAction.NotifyAndSuggest}
                      onChange={handleChangeMyPlaceProximityAction}
                      disabled={isMyPlaceProximityActionPending}
                    >
                      <option value={MyPlaceProximityAction.Off}>Off</option>
                      <option value={MyPlaceProximityAction.NotifyAndSuggest}>Notify & suggest check-in</option>
                      <option value={MyPlaceProximityAction.AutoCheckIn}>Auto check-in</option>
                    </select>
                  </div>
                </div>
                )}
              </div>
            </>
          )}

          {activeTab === 'blocks' && (
            <Table
              id="user-blocks"
              name="Blocks"
              keyField="_key"
              initialSortColumn="blockedAt"
              initialSortOrder="desc"
              columns={[
                { label: '', accessor: 'blockedUser.imageUrl', type: 'userAvatar' },
                {
                  label: 'Blocked user',
                  accessor: 'blockedUser.lastName',
                  type: 'fullNameAndTelephone',
                },
                { label: '', accessor: 'venue.logoUrl', type: 'venueAvatar' },
                {
                  label: 'Venue',
                  accessor: 'venue.name',
                  type: 'nameAndAddress',
                  className: 'width-30p',
                },
                { label: 'Blocked at', accessor: 'blockedAt', type: 'datetime' },
                { label: 'Type', accessor: 'reported', type: 'reported' },
              ]}
              sourceData={blocks}
              isPropertyBarVisible={false}
              filterTerms=""
              onRowClick={() => {}}
              disableGlobalSearch={true}
            />
          )}

          {activeTab === 'likes' && (
            <div className="user-detail-tab-table">
              {likesPagination.totalPages > 1 && (
                <div className="user-detail-tab-table-toolbar">
                  <Pagination
                    currentPage={likesPagination.page}
                    totalPages={likesPagination.totalPages}
                    onPageChange={handleLikesPageChange}
                  />
                </div>
              )}
              <Table
                id="user-likes"
                name="Likes"
                keyField="_key"
                initialSortColumn="likedAt"
                initialSortOrder="desc"
                columns={[
                  { label: '', accessor: 'likedUser.imageUrl', type: 'userAvatar' },
                  {
                    label: 'Liked user',
                    accessor: 'likedUser.lastName',
                    type: 'fullNameAndTelephone',
                  },
                  { label: '', accessor: 'venue.logoUrl', type: 'venueAvatar' },
                  {
                    label: 'Venue',
                    accessor: 'venue.name',
                    type: 'nameAndAddress',
                    className: 'width-30p',
                  },
                  { label: 'Liked at', accessor: 'likedAt', type: 'datetime' },
                ]}
                sourceData={likes}
                isPropertyBarVisible={false}
                filterTerms=""
                onRowClick={() => {}}
                disableGlobalSearch={true}
                disableClientSort={true}
              />
            </div>
          )}

          {activeTab === 'conversations' && (
            <div className="user-detail-tab-table">
              {conversationsPagination.totalPages > 1 && (
                <div className="user-detail-tab-table-toolbar">
                  <Pagination
                    currentPage={conversationsPagination.page}
                    totalPages={conversationsPagination.totalPages}
                    onPageChange={handleConversationsPageChange}
                  />
                </div>
              )}
              <Table
                id="user-conversations"
                name="Conversations"
                keyField="publicId"
                initialSortColumn="createdAt"
                initialSortOrder="desc"
                columns={[
                  { label: '', accessor: 'fromUser.imageUrl', type: 'userAvatar' },
                  { label: 'From', accessor: 'fromUser.lastName', type: 'fullNameAndTelephone' },
                  { label: '', accessor: 'toUser.imageUrl', type: 'userAvatar' },
                  { label: 'To', accessor: 'toUser.lastName', type: 'fullNameAndTelephone' },
                  { label: 'Initiated at', accessor: 'createdAt', type: 'datetime' },
                  { label: 'Duration', accessor: 'duration', type: 'interval' },
                ]}
                sourceData={conversations}
                isPropertyBarVisible={false}
                filterTerms=""
                onRowClick={handleConversationRowClick}
                disableGlobalSearch={true}
                disableClientSort={true}
              />
            </div>
          )}

          {activeTab === 'feedback' && (
            <div className="user-detail-tab-table">
              <Table
                id="user-feedback"
                name="Feedback"
                keyField="publicId"
                initialSortColumn="lastReplyAt"
                initialSortOrder="desc"
                columns={[
                  { label: 'Subject', accessor: 'subject', type: 'text' },
                  { label: 'Latest', accessor: 'lastReplyPreview', type: 'text' },
                  { label: 'Status', accessor: 'statusLabel', type: 'text' },
                  { label: 'Updated', accessor: 'lastReplyAt', type: 'datetime' },
                ]}
                sourceData={feedbackTopics}
                isPropertyBarVisible={false}
                filterTerms=""
                onRowClick={(row: CommentDto) => setSelectedFeedbackPublicId(row.publicId)}
                disableGlobalSearch={true}
                options={[
                  {
                    label: 'Close topic',
                    onClick: async (row: CommentDto) => {
                      if (row.closedAt) return
                      await adminService.closeFeedbackTopic(row.publicId)
                      fetchFeedbackTopics()
                    },
                  },
                ]}
              />
            </div>
          )}

          {activeTab === 'breadcrumbs' && (
            <div className="user-detail-tab-table">
              {breadcrumbsPagination.totalPages > 1 && (
                <div className="user-detail-tab-table-toolbar">
                  <Pagination
                    currentPage={breadcrumbsPagination.page}
                    totalPages={breadcrumbsPagination.totalPages}
                    onPageChange={handleBreadcrumbsPageChange}
                  />
                </div>
              )}
              <Table
                id="user-breadcrumbs"
                name="Breadcrumbs"
                keyField="_key"
                initialSortColumn="createdAt"
                initialSortOrder="desc"
                columns={[
                  { label: '', accessor: 'venue.logoUrl', type: 'venueAvatar' },
                  { label: 'Venue', accessor: 'venue.name', type: 'nameAndAddress', className: 'width-30p' },
                  { label: 'Type', accessor: 'type', type: 'enum' },
                  { label: 'When', accessor: 'createdAt', type: 'datetime' },
                  { label: 'Message', accessor: 'content', type: 'string' },
                  { label: 'Flagged', accessor: 'publicId', type: 'activityModerationFlags' },
                ]}
                sourceData={breadcrumbActivity}
                isPropertyBarVisible={false}
                filterTerms=""
                onRowClick={handleBreadcrumbRowClick}
                disableGlobalSearch={true}
                disableClientSort={true}
              />
            </div>
          )}

          {activeTab === 'stickers' && (
            <div className="user-detail-tab-table">
              {stickerActivityPagination.totalPages > 1 && (
                <div className="user-detail-tab-table-toolbar">
                  <Pagination
                    currentPage={stickerActivityPagination.page}
                    totalPages={stickerActivityPagination.totalPages}
                    onPageChange={handleStickerActivityPageChange}
                  />
                </div>
              )}
              <Table
                id="user-stickers"
                name="Stickers"
                keyField="publicId"
                initialSortColumn="createdAt"
                initialSortOrder="desc"
                columns={[
                  { label: '', accessor: 'imageUrl', type: 'thumbnail' },
                  { label: 'Venue', accessor: 'venue.name', type: 'text' },
                  { label: 'Caption', accessor: 'caption', type: 'text' },
                  { label: 'When', accessor: 'createdAt', type: 'datetime' },
                  { label: 'Flagged', accessor: 'isFlagged', type: 'flagged' },
                ]}
                sourceData={stickerActivity}
                isPropertyBarVisible={false}
                filterTerms=""
                onRowClick={(row: AdminStickerDto) => setSelectedStickerPublicId(row.publicId)}
                disableGlobalSearch={true}
                disableClientSort={true}
              />
            </div>
          )}

          {activeTab === 'tickets' && (
            <div className="user-detail-tab-table">
              {ticketsPagination.totalPages > 1 && (
                <div className="user-detail-tab-table-toolbar">
                  <Pagination
                    currentPage={ticketsPagination.page}
                    totalPages={ticketsPagination.totalPages}
                    onPageChange={setTicketsPage}
                  />
                </div>
              )}
              <Table
                id="user-tickets"
                name="Tickets"
                keyField="publicId"
                initialSortColumn="purchasedAt"
                initialSortOrder="desc"
                columns={[
                  { label: 'Event', accessor: 'event.name', type: 'text' },
                  { label: 'Venue', accessor: 'venue.name', type: 'text' },
                  { label: 'Event starts', accessor: 'eventStartDate', type: 'datetime' },
                  { label: 'Price', accessor: 'priceLabel', type: 'text' },
                  { label: 'Purchased', accessor: 'purchasedAt', type: 'datetime' },
                ]}
                sourceData={tickets}
                isPropertyBarVisible={false}
                filterTerms=""
                onRowClick={() => {}}
                disableGlobalSearch={true}
                disableClientSort={true}
              />
            </div>
          )}

          {activeTab === 'visits' && (
            <div className="user-detail-tab-table">
              {visitsPagination.totalPages > 1 && (
                <div className="user-detail-tab-table-toolbar">
                  <Pagination
                    currentPage={visitsPagination.page}
                    totalPages={visitsPagination.totalPages}
                    onPageChange={handleVisitsPageChange}
                  />
                </div>
              )}
              <Table
                id="user-visits"
                name="Visits"
                keyField="publicId"
                initialSortColumn="checkedInAt"
                initialSortOrder="desc"
                columns={[
                  { label: '', accessor: 'venue.logoUrl', type: 'venueAvatar' },
                  { label: 'Venue', accessor: 'venue.name', type: 'nameAndAddress', className: 'width-30p' },
                  { label: 'Check-in', accessor: 'checkedInAt', type: 'datetime' },
                  { label: 'Check-out', accessor: 'checkedOutAt', type: 'datetime' },
                  { label: 'Duration', accessor: 'secondsInVenue', type: 'interval' },
                  { label: 'Checkout type', accessor: 'checkoutType', type: 'checkoutType' },
                ]}
                sourceData={visits}
                isPropertyBarVisible={false}
                filterTerms=""
                onRowClick={handleVisitsRowClick}
                disableGlobalSearch={true}
                disableClientSort={true}
              />
            </div>
          )}

          {activeTab === 'my-places' && (
            <div className="user-detail-tab-table">
              <p style={{ marginBottom: 12 }}>
                Notifications: {myPlaces?.notificationsEnabled === false ? 'off' : 'on'}
              </p>
              <Table
                id="user-my-places"
                name="My Places"
                keyField="_key"
                columns={[
                  { label: '', accessor: 'venue.logoUrl', type: 'venueAvatar' },
                  { label: 'Venue', accessor: 'venue.name', type: 'nameAndAddress', className: 'width-30p' },
                  { label: 'Pinned', accessor: 'pinnedAt', type: 'datetime' },
                ]}
                sourceData={(myPlaces?.pinned ?? []).map(item => ({ ...item, _key: `pinned-${item.venue.publicId}` }))}
                isPropertyBarVisible={false}
                filterTerms=""
                onRowClick={() => {}}
                disableGlobalSearch={true}
                disableClientSort={true}
              />
            </div>
          )}

          {activeTab === 'audit-logs' && (
            <div className="user-detail-tab-table">
              {auditLogsPagination.totalPages > 1 && (
                <div className="user-detail-tab-table-toolbar">
                  <Pagination
                    currentPage={auditLogsPagination.page}
                    totalPages={auditLogsPagination.totalPages}
                    onPageChange={handleAuditLogsPageChange}
                  />
                </div>
              )}
              <Table
                id="user-audit-logs"
                name="Audit logs"
                keyField="publicId"
                initialSortColumn="createdAt"
                initialSortOrder="desc"
                columns={[
                  { label: 'Created', accessor: 'createdAt', type: 'datetime' },
                  { label: 'Event', accessor: 'eventType', type: 'enum', enumName: 'AuditLogEventType' },
                  { label: 'Producer', accessor: 'producer', type: 'enum', enumName: 'AuditLogProducer' },
                  { label: 'Performed by', accessor: 'performedByFullName', type: 'text' as any },
                  { label: 'Affected user', accessor: 'affectedUserFullName', type: 'text' as any },
                  { label: 'Comment', accessor: 'comment', type: 'text' as any },
                ]}
                sourceData={auditLogs}
                isPropertyBarVisible={false}
                filterTerms=""
                onRowClick={() => {}}
                disableGlobalSearch={true}
                disableClientSort={true}
              />
            </div>
          )}

          {activeTab === 'vibe-history' && (
            <div className="user-detail-tab-table">
              {vibeHistoryPagination.totalPages > 1 && (
                <div className="user-detail-tab-table-toolbar">
                  <Pagination
                    currentPage={vibeHistoryPagination.page}
                    totalPages={vibeHistoryPagination.totalPages}
                    onPageChange={handleVibeHistoryPageChange}
                  />
                </div>
              )}
              <Table
                id="user-vibe-history"
                name="Vibe history"
                keyField="publicId"
                initialSortColumn="createdAt"
                initialSortOrder="desc"
                columns={[
                  { label: 'Created', accessor: 'createdAt', type: 'datetime' },
                  { label: 'Emoji', accessor: 'data.emoji', type: 'text' as any },
                  { label: 'Venue', accessor: 'data.venueName', type: 'text' as any },
                ]}
                sourceData={vibeHistory}
                isPropertyBarVisible={false}
                filterTerms=""
                onRowClick={() => {}}
                disableGlobalSearch={true}
                disableClientSort={true}
              />
            </div>
          )}

          {activeTab === 'intentions' && (
            <div className="user-detail-tab-table">
              <div className="user-detail-info grid" style={{ marginBottom: '24px' }}>
                {userDetail.currentIntention ? (
                  <>
                    <FormItem label="Current intention" value={`${userDetail.currentIntention.emoji} ${userDetail.currentIntention.name}`} />
                    <FormItem label="Prompt" value={userDetail.currentIntention.prompt} />
                    <FormItem label="Last updated" value={userDetail.intentionUpdatedAt} type="datetime" />
                  </>
                ) : (
                  <FormItem label="Current intention" value="None" />
                )}
              </div>
              {intentionHistoryPagination.totalPages > 1 && (
                <div className="user-detail-tab-table-toolbar">
                  <Pagination
                    currentPage={intentionHistoryPagination.page}
                    totalPages={intentionHistoryPagination.totalPages}
                    onPageChange={handleIntentionHistoryPageChange}
                  />
                </div>
              )}
              <Table
                id="user-intention-history"
                name="Intention history"
                keyField="publicId"
                initialSortColumn="createdAt"
                initialSortOrder="desc"
                columns={[
                  { label: 'Created', accessor: 'createdAt', type: 'datetime' },
                  { label: 'Intention', accessor: 'data.name', type: 'text' as any },
                  { label: 'Prompt', accessor: 'data.prompt', type: 'text' as any },
                  { label: 'Emoji', accessor: 'data.emoji', type: 'text' as any },
                  { label: 'Venue', accessor: 'data.venueName', type: 'text' as any },
                ]}
                sourceData={intentionHistory}
                isPropertyBarVisible={false}
                filterTerms=""
                onRowClick={() => {}}
                disableGlobalSearch={true}
                disableClientSort={true}
              />
            </div>
          )}

          {activeTab === 'photo-verification' && (
            <div className="user-detail-tab-table">
              <div className="user-photo-verification-status">
                <span className="user-photo-verification-status__label">Current photo verified</span>
                <span
                  className={`user-photo-verification-status__badge ${
                    userDetail.isPhotoVerified
                      ? 'user-photo-verification-status__badge--verified'
                      : 'user-photo-verification-status__badge--unverified'
                  }`}
                >
                  {userDetail.isPhotoVerified ? 'Yes' : 'No'}
                </span>
              </div>
              {photoVerificationPagination.totalPages > 1 && (
                <div className="user-detail-tab-table-toolbar">
                  <Pagination
                    currentPage={photoVerificationPagination.page}
                    totalPages={photoVerificationPagination.totalPages}
                    onPageChange={handlePhotoVerificationPageChange}
                  />
                </div>
              )}
              <Table
                id="user-photo-verification-attempts"
                name="Photo verification attempts"
                keyField="publicId"
                initialSortColumn="createdAt"
                initialSortOrder="desc"
                columns={[
                  { label: '', accessor: 'currentImageUrl', type: 'avatar' },
                  { label: 'Context', accessor: 'context', type: 'string' },
                  { label: 'Result', accessor: 'statusLabel', type: 'string' },
                  { label: 'Current avatar', accessor: 'isCurrentAvatarLabel', type: 'string' },
                  { label: 'Can verify', accessor: 'canManuallyVerifyLabel', type: 'string' },
                  { label: 'Created', accessor: 'createdAt', type: 'datetime' },
                ]}
                sourceData={photoVerificationAttempts.map(toPhotoVerificationAttemptRow)}
                isPropertyBarVisible={false}
                filterTerms=""
                onRowClick={(row: { publicId: string }) => {
                  const attempt = photoVerificationAttempts.find(item => item.publicId === row.publicId)
                  if (attempt) openPhotoAttemptDetail(attempt)
                }}
                disableGlobalSearch={true}
                disableClientSort={true}
              />
            </div>
          )}
        </div>
      )}
      <Modal
        isOpen={!!selectedVisit && !!visitDetail}
        onRequestClose={handleCloseVisitModal}
        className="visit-map-popup"
        overlayClassName="modal-overlay"
        closeTimeoutMS={300}
      >
        {visitDetail && (
          <div className="visit-map-popup-content">
            <div className="visit-map-popup-map-container">
              <VisitMap
                latitude={visitDetail.venue.latitude}
                longitude={visitDetail.venue.longitude}
                northeastLatitude={visitDetail.venue.northeastLatitude}
                northeastLongitude={visitDetail.venue.northeastLongitude}
                southwestLatitude={visitDetail.venue.southwestLatitude}
                southwestLongitude={visitDetail.venue.southwestLongitude}
                pings={visitDetail.pings || null}
              />
            </div>
          </div>
        )}
      </Modal>

      <ConversationDetailPopup
        conversationPublicId={selectedConversationPublicId}
        isOpen={isConversationPopupVisible}
        onClose={handleCloseConversationPopup}
      />

      <FeedbackThreadModal
        commentPublicId={selectedFeedbackPublicId}
        isOpen={!!selectedFeedbackPublicId}
        onClose={() => setSelectedFeedbackPublicId(null)}
        onChanged={fetchFeedbackTopics}
        showUserProfileLink={false}
      />

      <UserBreadcrumbActivityModal
        isOpen={!!selectedActivity}
        onClose={handleCloseBreadcrumbModal}
        activity={selectedActivity}
        userName={
          userDetail?.firstName && userDetail?.lastName
            ? `${userDetail.firstName} ${userDetail.lastName}`
            : undefined
        }
        onModerationChange={() => fetchBreadcrumbActivity()}
        onDelete={async (breadcrumbPublicId: string) => {
          await adminService.deleteVenueBreadcrumb(breadcrumbPublicId)
        }}
        onDeleted={() => {
          handleCloseBreadcrumbModal()

          const isLastItemOnPage = breadcrumbActivity.length === 1
          if (isLastItemOnPage && breadcrumbsPage > 1) {
            setBreadcrumbsPage(breadcrumbsPage - 1)
            return
          }

          fetchBreadcrumbActivity()
        }}
      />

      <StickerModal
        isOpen={!!selectedStickerPublicId}
        onClose={() => setSelectedStickerPublicId(null)}
        publicId={selectedStickerPublicId}
        onModerationChange={() => fetchStickerActivity()}
        onDelete={async (stickerPublicId: string) => {
          await adminService.deleteSticker(stickerPublicId)
        }}
        onDeleted={() => {
          setSelectedStickerPublicId(null)

          const isLastItemOnPage = stickerActivity.length === 1
          if (isLastItemOnPage && stickerActivityPage > 1) {
            setStickerActivityPage(stickerActivityPage - 1)
            return
          }

          fetchStickerActivity()
        }}
      />

      <UnlockUserModal
        isOpen={isUnlockModalOpen}
        onClose={() => setIsUnlockModalOpen(false)}
        publicId={publicId ?? ''}
        updateUser={(state) => setUserDetail(state)}
      ></UnlockUserModal>
      <UserPhotoModal
        isOpen={isPhotoModalOpen}
        onClose={() => setIsPhotoModalOpen(false)}
        userPublicId={publicId ?? ''}
        // A removed photo is the placeholder — nothing left to remove.
        hasPhoto={!!userDetail?.imageUrl && !userDetail?.isPhotoRejectedByAdmin}
        onSaved={setUserDetail}
      />
      <ClearLoginFailedAttemptsModal
        isOpen={isClearLoginFailedAttemptsModalOpen}
        onClose={() => setIsClearLoginFailedAttemptsModalOpen(false)}
        publicId={publicId ?? ''}
        updateUser={(state) => setUserDetail(state)}
      />
      <LockUserModal
        isOpen={isLockModalOpen}
        onClose={() => setIsLockModalOpen(false)}
        publicId={publicId ?? ''}
        updateUser={(state) => setUserDetail(state)}
      />
      <SendTestPushModal
        isOpen={isTestPushModalOpen}
        onClose={() => setIsTestPushModalOpen(false)}
        userPublicId={publicId ?? ''}
        userName={
          userDetail?.firstName && userDetail?.lastName
            ? `${userDetail.firstName} ${userDetail.lastName}`
            : undefined
        }
        hasDeviceToken={!!userDetail?.hasDeviceToken}
        hasValidOneSignalToken={!!userDetail?.hasValidOneSignalToken}
      />
      {userDetail && (
        <AccountAccessModal
          isOpen={isAccountAccessModalOpen}
          onClose={() => setIsAccountAccessModalOpen(false)}
          publicId={publicId ?? ''}
          kind={userDetail.kind}
          rolePublicId={userDetail.roles[0]?.publicId ?? ''}
          onSaved={setUserDetail}
        />
      )}

      <PhotoVerificationAttemptDetailModal
        selected={selectedPhotoAttempt}
        detailLoading={photoAttemptDetailLoading}
        verifying={photoAttemptVerifying}
        actionError={photoAttemptActionError}
        showUserLink={false}
        onClose={closePhotoAttemptDetail}
        onManualVerify={handleManualVerifyPhotoAttempt}
      />
    </div>
  )
}

export default UserDetailPage
